import { useEffect, useState } from 'react';
import { machineListCache } from 'App/Activity/MachineStateManager';
import { PowerState, SummaryState } from 'App/Activity/Network/ActionsUtil';
import {
	MachineData,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { isInMaintenanceMode } from 'App/Activity/Utility';
import {
	DesktopOSType,
	DesktopWallpaper,
	getDesktopOSType,
	getOSDisplayName,
} from 'App/Tile/utils';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export interface DesktopDetails {
	desktopWallpaper: DesktopWallpaper;
	/** Normalized display name for the desktop OS (e.g. 'Windows 10') */
	desktopOS?: string;
	/** Raw osType string from the backend (e.g. 'Ubuntu 20.04', 'Windows 10') */
	osType?: string;
	/** Raw osVersion string from the backend. */
	osVersion?: string;
	maintenanceMode: boolean;
	maintenanceModeUserString?: string;
	summaryState?: SummaryState;
	lastLaunchedTime?: string;
	isUnderMaintenanceOrUnavailable: boolean;
	isLoading?: boolean;
}

export const useDesktopDetails = (resource: Resource): DesktopDetails => {
	const [desktopWallpaper, setDesktopWallpaper] = useState<DesktopWallpaper>(
		DesktopOSType.Unknown
	);
	const [desktopOS, setDesktopOS] = useState<string>();
	const [desktopData, setDesktopData] = useState<MachineData>();
	const context = useResourceManagerContext();

	useEffect(() => {
		const machineData = machineListCache
			.getAllMachines()
			?.machines.find(machine => machine.resourceId === resource.id)?.machineData;
		setDesktopData(machineData);
		const osInput =
			`${machineData?.osType ?? ''} ${machineData?.osVersion ?? ''}`.trim() || undefined;
		const desktopOSType = getDesktopOSType(osInput);

		const getWallpaper = (): DesktopWallpaper => {
			if (
				context?.machineStatus?.loading &&
				!machineData?.osType &&
				!machineData?.osVersion
			) {
				return PowerState.LOADING as DesktopWallpaper;
			}
			if (isInMaintenanceMode(machineData)) {
				return 'UnderMaintenance';
			}
			if (machineData?.powerState === PowerState.UNAVAILABLE) {
				return PowerState.UNAVAILABLE as DesktopWallpaper;
			}
			return desktopOSType;
		};

		setDesktopOS(getOSDisplayName(machineData?.osType, desktopOSType));
		setDesktopWallpaper(getWallpaper());
	}, [context?.machineStatus?.loading, resource]);

	return {
		desktopWallpaper,
		desktopOS,
		osType: desktopData?.osType,
		osVersion: desktopData?.osVersion,
		maintenanceMode: isInMaintenanceMode(desktopData),
		maintenanceModeUserString: desktopData?.maintenanceModeUserString,
		summaryState: desktopData?.summaryState,
		lastLaunchedTime: desktopData?.lastLaunchedTime,
		isUnderMaintenanceOrUnavailable:
			desktopWallpaper === 'Unavailable' || desktopWallpaper === 'UnderMaintenance',
		isLoading: context?.machineStatus?.loading,
	};
};
